import React from 'react';
import AccountSettings, {
  AccountSettingsTabComponent,
  Tab,
} from 'src/components/AccountSettings/AccountSettings';
import {ACCOUNT_SETTING_ROUTES} from 'src/components/AccountSettings/utils/utils';
import {useDispatch} from 'react-redux';
import {NOTIFICATION_SETTINGS} from 'shared/account-settings/actions/notification-settings';
import ContactInfoForm from 'shared/account-settings/components/ContactInfoForm';
import {SharedAccountSettings} from 'src/components/AccountSettings/AccountSettingsContext';
import {STUDENT_FIELDS} from '../../../../shared/account-settings/actions';
import {TransferAccountSettingsService as SettingsService} from 'src/services/account-settings-service';
import {submitForm} from '../../../user/actions';
import {getMedia} from '../../../utils/mq';
import PrivacyForm from '../../high-school/account-settings/PrivacyForm';
import Connections from '../../high-school/account-settings/containers/connections';
import ProfileForm from './profile-form/profile-form';

import {CommunityCollegeInfo, CurrentInstitution} from 'src/types/store';
import {
  Ethnicity,
  Gender,
  IsUsCitizenOrResident,
  Sex,
} from 'src/components/AccountSettings/profile-form/utils';

export type TransferAccountSettings = SharedAccountSettings & {
  _id: string;
  firstName: string;
  lastName: string;
  gender: Gender;
  sex: Sex;
  isUsCitizenOrResident: IsUsCitizenOrResident;
  religion: string;
  isFirstGen: boolean;
  ethnicity: Ethnicity;
  addressLine1: string;
  addressLine2: string;
  city: string;
  subdivision: string;
  country: string;
  postalCode: string;
  birthdayDay: number;
  birthdayMonth: number;
  birthdayYear: number;
  currentInstitution: CurrentInstitution;
  communityCollegeInfo: CommunityCollegeInfo;
  confirmedUnverifiedAddress: boolean;
  addressVerified: boolean;
  autoConnectCounselors: boolean;
};

const tabs: Tab<TransferAccountSettings>[] = [
  {
    ...ACCOUNT_SETTING_ROUTES.profile,
    component: function ProfileTab({
      settings,
      saveSettings,
      ...props
    }: AccountSettingsTabComponent<TransferAccountSettings>) {
      return <ProfileForm initialValues={settings} onSubmit={saveSettings} {...props} />;
    },
  },
  {
    ...ACCOUNT_SETTING_ROUTES.contact,
    component: function ContactInfoTab({
      settings,
      saveSettings,
      setNavigationAnnouncement,
      ...props
    }: AccountSettingsTabComponent<TransferAccountSettings>): React.ReactElement {
      return (
        <ContactInfoForm
          initialValues={settings}
          // @ts-expect-error
          onSubmit={saveSettings}
          setNavigationAnnouncement={setNavigationAnnouncement}
          {...props}
        />
      );
    },
  },
  {
    ...ACCOUNT_SETTING_ROUTES.privacy,
    component: () => (
      <PrivacyForm
        connectionSettingTitle="For community college students only - connect automatically with college counselors"
        connectionLabel="Connect automatically with college counselors"
      />
    ),
  },
  {
    ...ACCOUNT_SETTING_ROUTES.connect,
    component: function ConnectionsTab({
      settings,
      saveSettings,
      setNavigationAnnouncement,
      ...props
    }: AccountSettingsTabComponent<TransferAccountSettings>): React.ReactElement {
      return (
        <Connections
          initialValues={settings}
          onSubmit={saveSettings}
          setNavigationAnnouncement={setNavigationAnnouncement}
          description="Manage your connections with counselors at your community college. Connecting to your
          counselor helps them to give you the best advice and recommendations possible because they are able
          to view your portfolio, scholarship awards, and the colleges you're following."
          {...props}
        />
      );
    },
  },
];

const fetchAccountSettings = async (): Promise<TransferAccountSettings> => {
  const settings = await SettingsService.show('me', {
    fields: `${STUDENT_FIELDS},notificationSettings`,
  });
  if (settings.currentInstitution.campus) {
    // campus should be a Campus object, but it's being returned as a string
    const campus = `${settings.currentInstitution.campus}`;
    settings.currentInstitution.campus = JSON.parse(campus)._id;
  }

  return settings;
};

const TransferAccountSettings = (): React.ReactElement => {
  const dispatch = useDispatch();

  return (
    <AccountSettings<TransferAccountSettings>
      tabs={tabs}
      fetchAccountSettings={fetchAccountSettings}
      availableNotificationSettings={NOTIFICATION_SETTINGS}
      saveSettings={(
        newSettings: Partial<TransferAccountSettings>,
        previousSettings?: TransferAccountSettings | null
      ): Promise<TransferAccountSettings> => {
        const shouldValidateAddress =
          !previousSettings?.confirmedUnverifiedAddress && !previousSettings?.addressVerified;
        return submitForm(
          newSettings,
          '/v1/users/',
          getMedia().MS_AND_DOWN,
          dispatch,
          shouldValidateAddress,
          true,
          true
        );
      }}
    />
  );
};

export default TransferAccountSettings;
